'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';
import { ContactForm } from '@/components/contact/ContactForm';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { contactCopy } from '@/lib/i18n/pages/contact';

export function ContactPageClient() {
  const { locale, dir, href } = useLocale();
  const isRtl = dir === 'rtl';
  const t = pageCopy(contactCopy, locale);
  // Arabic-script needs 13px minimum and no tracking (breaks letter joining).
  const sz = (px: number) => (isRtl ? '13px' : `${px}px`);
  const ls = (v: string) => (isRtl ? 'normal' : v);
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const formSectionRef = useRef<HTMLElement>(null);
  const projectsTitleRef = useRef<HTMLHeadingElement>(null);
  const projectsSubtitleRef = useRef<HTMLParagraphElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    if (isRtl) return;
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const ctx = gsap.context(() => {
      // Animate hero title letters
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);

        shuffledLetters.forEach((letter, index) => {
          gsap.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
            delay: 0.3 + index * 0.03,
          });
        });

        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1500);
      }

      // Animate subtitle
      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(subtitleRef.current, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          delay: 1.2,
          ease: 'power3.out',
        });
      }

      // Animate projects CTA section on scroll
      if (projectsSubtitleRef.current) {
        gsap.set(projectsSubtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(projectsSubtitleRef.current, {
          scrollTrigger: {
            trigger: projectsSubtitleRef.current,
            start: 'top 85%',
          },
          y: 0,
          opacity: 0.5,
          duration: 0.7,
          ease: 'power3.out',
        });
      }

      const projectsLetters = projectsTitleRef.current?.querySelectorAll('.projects-letter');
      if (projectsLetters) {
        gsap.set(projectsLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(projectsLetters, {
          scrollTrigger: {
            trigger: projectsTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.03,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            applyHoverEffect(projectsLetters);
          },
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, [locale]);

  const heroTitle = t.hero.title;

  return (
    <div ref={heroRef}>
      {/* ─── Hero Section ─── */}
      <section
        // 100dvh (not min-h-screen/100vh): tracks the real visible viewport on
        // iOS so the hero fills the screen edge-to-edge with no gap above or
        // below the browser chrome.
        className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#173835' }}
      >
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/videos/world-map.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#173835]/70" />
        </div>

        {/* Small label */}
        <span className="eyebrow relative z-[2]" style={{ marginBottom: '1.5rem' }}>
          {t.hero.eyebrow}
        </span>

        {/* Large CONTACT title with letter animation */}
        <h1
          ref={titleRef}
          className="font-six-caps rtl-display-l text-[calc(1rem+20vw)] leading-[0.85] text-white lowercase text-center relative z-[2]"
        >
          {(isRtl ? [heroTitle] : heroTitle.split('')).map((char, i) => (
            <span
              key={i}
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="relative z-[2] text-center text-white/60 leading-relaxed"
          style={{
            marginTop: 'clamp(2.5rem, 6vw, 5rem)',
            fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)',
            maxWidth: '40rem',
            paddingInlineStart: '2rem',
            paddingInlineEnd: '2rem',
          }}
        >
          {t.hero.subtitle}
        </p>

        {/* Bottom navigation */}
        <div
          className="absolute start-0 end-0 z-[2] flex items-end justify-between"
          style={{
            bottom: 'calc(clamp(2rem, 5vw, 3.5rem) + env(safe-area-inset-bottom, 0px))',
            paddingInlineStart: 'clamp(1.5rem, 4vw, 4rem)',
            paddingInlineEnd: 'clamp(1.5rem, 4vw, 4rem)',
          }}
        >
          <button
            onClick={() => formSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center cursor-pointer transition-opacity duration-300 hover:opacity-70"
            style={{ gap: '0.75rem', background: 'none', border: 'none', padding: 0 }}
          >
            <span className="uppercase text-white/40 tracking-widest" style={{ fontSize: sz(11), letterSpacing: ls('0.2em') }}>{t.hero.scroll}</span>
            <svg
              className="text-white/40"
              style={{ width: '14px', height: '14px' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <a
            href="#connect"
            className="transition-opacity duration-300 hover:opacity-70 uppercase text-white/40 tracking-widest"
            style={{ fontSize: sz(11), letterSpacing: ls('0.2em'), textDecoration: 'none' }}
          >
            {t.hero.connect}
          </a>
        </div>
      </section>

      {/* ─── Two-Column: Contact Info + Form ─── */}
      <section
        ref={formSectionRef}
        id="connect"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: 'clamp(5rem, 12vw, 10rem)',
          paddingBottom: 'clamp(5rem, 12vw, 10rem)',
          paddingInlineStart: 'clamp(1.5rem, 8vw, 120px)',
          paddingInlineEnd: 'clamp(1.5rem, 8vw, 120px)',
        }}
      >
        <div
          className="grid grid-cols-1 lg:grid-cols-2"
          style={{ maxWidth: '1200px', marginInline: 'auto', gap: 'clamp(3rem, 8vw, 8rem)' }}
        >
          {/* ── Left Column: Info ── */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <p
              className="uppercase text-white/55 tracking-widest"
              style={{ fontSize: sz(10), letterSpacing: ls('0.3em'), marginBottom: '1.5rem' }}
            >
              {t.info.eyebrow}
            </p>

            <h2
              className="font-six-caps rtl-display-l text-white lowercase"
              style={{ fontSize: 'clamp(5rem, 12vw, 10rem)', lineHeight: '0.85', marginBottom: 'clamp(2rem, 4vw, 3rem)' }}
            >
              {(isRtl ? [t.info.title] : t.info.title.split('')).map((char, i) => (
                <span
                  key={i}
                  className={`inline-block ${char === ' ' ? 'mx-2 md:mx-4' : ''}`}
                  style={{ transformOrigin: 'center bottom' }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </h2>

            <div style={{ width: '4rem', height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', marginBottom: '2.5rem' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Email */}
              <div>
                <p
                  className="uppercase text-white/55 tracking-widest"
                  style={{ fontSize: sz(10), letterSpacing: ls('0.3em'), marginBottom: '0.5rem' }}
                >
                  {t.info.emailLabel}
                </p>
                <a
                  href="mailto:info@datacode.app"
                  className="text-white hover:text-white/70 transition-colors"
                  style={{ fontSize: '1.0625rem', textDecoration: 'none' }}
                >
                  <bdi dir="ltr">info@datacode.app</bdi>
                </a>
              </div>

              {/* Phone */}
              <div>
                <p
                  className="uppercase text-white/55 tracking-widest"
                  style={{ fontSize: sz(10), letterSpacing: ls('0.3em'), marginBottom: '0.5rem' }}
                >
                  {t.info.phoneLabel}
                </p>
                <a
                  href="tel:+9647514491008"
                  className="text-white hover:text-white/70 transition-colors"
                  style={{ fontSize: '1.0625rem', textDecoration: 'none' }}
                >
                  <bdi dir="ltr">+964 751 449 1008</bdi>
                </a>
              </div>

              {/* Address */}
              <div>
                <p
                  className="uppercase text-white/55 tracking-widest"
                  style={{ fontSize: sz(10), letterSpacing: ls('0.3em'), marginBottom: '0.5rem' }}
                >
                  {t.info.addressLabel}
                </p>
                <p className="text-white" style={{ fontSize: '1.0625rem', lineHeight: '1.6' }}>
                  {t.info.addressLine1}<br />
                  {t.info.addressLine2}
                </p>
              </div>
            </div>
          </div>

          {/* ── Right Column: Form ── */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* ─── Cinematic Map Section ─── */}
      <section style={{ position: 'relative', overflow: 'hidden', height: 'clamp(400px, 50vh, 600px)', backgroundColor: '#0a0a0a' }}>
        {/* Google Maps — atmospheric background */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6000!2d44.0127!3d36.2095!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x5ed2c5f3e9876fa3!2sDataCode!5e1!4m2!3m1!1s0x0%3A0x5ed2c5f3e9876fa3"
            width="100%"
            height="100%"
            style={{
              border: 0,
              display: 'block',
              filter: 'grayscale(1) brightness(0.2) contrast(1.2)',
              transform: 'scale(1.1)',
              pointerEvents: 'none',
            }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={t.map.iframeTitle}
          />
        </div>

        {/* Dark green tint overlay */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(135deg, rgba(23,56,53,0.5) 0%, rgba(12,12,12,0.3) 50%, rgba(23,56,53,0.4) 100%)',
        }} />

        {/* Top gradient blend */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
          height: '180px',
          background: 'linear-gradient(to bottom, #0c0c0c 15%, transparent)',
        }} />
        {/* Bottom gradient blend */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
          height: '120px',
          background: 'linear-gradient(to top, #0c0c0c, transparent)',
        }} />
        {/* Left gradient (hides Maps popup) */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0, left: 0, zIndex: 2,
          width: '25%',
          background: 'linear-gradient(to right, #0c0c0c 30%, transparent)',
        }} />
        {/* Right gradient */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0, right: 0, zIndex: 2,
          width: '15%',
          background: 'linear-gradient(to left, #0c0c0c, transparent)',
        }} />

        {/* Large "ERBIL" watermark */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span
            className="font-six-caps rtl-display-l"
            style={{
              fontSize: 'clamp(8rem, 22vw, 20rem)',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.06)',
              letterSpacing: '0.1em',
              userSelect: 'none',
            }}
          >
            {t.map.watermark}
          </span>
        </div>

        {/* Pulsing location marker */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%', zIndex: 4,
          transform: 'translate(-50%, -50%)',
        }}>
          <div className="pulse-ring" style={{
            position: 'absolute', top: '50%', left: '50%',
            width: '60px', height: '60px',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.15)',
            transform: 'translate(-50%, -50%)',
          }} />
          <div className="pulse-dot" style={{
            width: '12px', height: '12px',
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            boxShadow: '0 0 20px rgba(255,255,255,0.4)',
          }} />
        </div>

        {/* Frosted glass card — bottom left */}
        <div style={{
          position: 'absolute', bottom: 'clamp(2rem, 5vw, 3.5rem)', insetInlineStart: 'clamp(1.5rem, 8vw, 120px)', zIndex: 5,
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: 'clamp(1.25rem, 2vw, 1.75rem) clamp(1.5rem, 3vw, 2.5rem)',
          }}>
            <p className="uppercase text-white/55 tracking-widest" style={{ fontSize: sz(9), letterSpacing: ls('0.25em'), marginBottom: '0.5rem' }}>
              {t.map.officeLabel}
            </p>
            <p className="text-white" style={{ fontSize: '0.9375rem', lineHeight: '1.5', marginBottom: '0.75rem' }}>
              {t.info.addressLine1}<br />
              {t.info.addressLine2}
            </p>
            <a
              href="https://www.google.com/maps/place/DataCode/data=!4m2!3m1!1s0x0:0x5ed2c5f3e9876fa3"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center transition-opacity duration-300 hover:opacity-70"
              style={{ gap: '0.5rem' }}
            >
              <span className="uppercase text-white/50 transition-colors duration-300 group-hover:text-white/80 tracking-widest" style={{ fontSize: sz(10), letterSpacing: ls('0.2em') }}>
                {t.map.getDirections}
              </span>
              <svg className="rtl-mirror text-white/50 transition-colors duration-300 group-hover:text-white/80" style={{ width: '12px', height: '12px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </div>

        {/* Coordinates tag — bottom right */}
        <div style={{
          position: 'absolute', bottom: 'clamp(2rem, 5vw, 3.5rem)', insetInlineEnd: 'clamp(1.5rem, 8vw, 120px)', zIndex: 5,
        }}>
          <span className="text-white/20" dir="ltr" style={{ fontSize: sz(11), fontFamily: 'monospace', letterSpacing: '0.05em', display: 'inline-block' }}>
            36.2095°N, 44.0127°E
          </span>
        </div>
      </section>

      {/* ─── Projects CTA Section ─── */}
      <section
        className="relative flex flex-col items-center justify-center"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: 'clamp(5rem, 12vw, 10rem)',
          paddingBottom: 'clamp(5rem, 12vw, 10rem)',
        }}
      >
        <p
          ref={projectsSubtitleRef}
          className="text-center uppercase text-white/40 tracking-widest"
          style={{
            fontSize: sz(11),
            letterSpacing: ls('0.25em'),
            marginBottom: 'clamp(1.5rem, 3vw, 2.5rem)',
          }}
        >
          {t.cta.eyebrow}
        </p>

        <Link href={href('/portfolio')} className="group">
          <h2
            ref={projectsTitleRef}
            className="font-six-caps rtl-display-l text-white lowercase text-center transition-all duration-500 group-hover:opacity-80"
            style={{
              fontSize: 'calc(1rem + 18vw)',
              lineHeight: '0.85',
              transform: 'scale(1)',
              transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {(isRtl ? [t.cta.title] : t.cta.title.split('')).map((char, i) => (
              <span
                key={i}
                className="projects-letter inline-block"
                style={{ transformOrigin: 'center bottom' }}
              >
                {char}
              </span>
            ))}
          </h2>
        </Link>
      </section>

      <style>{`
        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
          100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
        }
        @keyframes pulse-dot {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        .pulse-ring { animation: pulse-ring 2.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite; }
        .pulse-dot { animation: pulse-dot 2.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
